import React, { useState, useRef, useCallback } from 'react';
import { X, Loader2, Copy, Check, FileDown, FileText, Briefcase, ClipboardList } from 'lucide-react';
import MarkdownMessage from './markdown-message';
import { useYenomAIStore } from '../../state/yenomAIStore';

const MEMO_TYPES = [
  { key: 'ic_memo', label: 'IC Memo', icon: FileText, desc: 'Investment committee format with thesis, metrics, risks' },
  { key: 'client_brief', label: 'Client Brief', icon: Briefcase, desc: 'Client-ready summary with action items' },
  { key: 'investment_note', label: 'Investment Note', icon: ClipboardList, desc: 'Structured research note with financials' },
];

export default function MemoModal({ open, onClose, conversationId }) {
  const [selectedType, setSelectedType] = useState('ic_memo');
  const [copied, setCopied] = useState(false);
  const contentRef = useRef(null);

  const memo = useYenomAIStore(s => s.memo);
  const memoLoading = useYenomAIStore(s => s.memoLoading);
  const memoError = useYenomAIStore(s => s.memoError);
  const generateMemo = useYenomAIStore(s => s.generateMemo);
  const clearMemo = useYenomAIStore(s => s.clearMemo);

  const handleGenerate = useCallback(() => {
    if (!conversationId) return;
    generateMemo(conversationId, selectedType);
  }, [conversationId, selectedType, generateMemo]);

  const handleClose = useCallback(() => {
    clearMemo();
    onClose();
  }, [clearMemo, onClose]);

  const handleCopy = async () => {
    if (!memo?.memo) return;
    try {
      await navigator.clipboard.writeText(memo.memo);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  const handleExportPdf = useCallback(() => {
    if (!contentRef.current) return;
    import('../../utils/exportPdf').then(({ exportElementAsPdf }) => {
      const typeLabel = MEMO_TYPES.find(t => t.key === (memo?.memo_type || selectedType))?.label || 'Memo';
      exportElementAsPdf(contentRef.current, `KubAIr ${typeLabel}`);
    });
  }, [memo, selectedType]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-background border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-500" />
            <h2 className="text-sm font-semibold">Generate Memo</h2>
          </div>
          <button onClick={handleClose} className="p-1 rounded-md hover:bg-secondary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {!memo ? (
            <>
              {/* Type selector */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Choose memo format</p>
                <div className="grid grid-cols-1 gap-2">
                  {MEMO_TYPES.map(({ key, label, icon: Icon, desc }) => (
                    <button
                      key={key}
                      onClick={() => setSelectedType(key)}
                      className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                        selectedType === key
                          ? 'border-emerald-500 bg-emerald-500/5'
                          : 'border-border hover:border-border/80 hover:bg-secondary/30'
                      }`}
                    >
                      <Icon className={`w-4 h-4 mt-0.5 ${selectedType === key ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                      <div>
                        <p className={`text-sm font-medium ${selectedType === key ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>{label}</p>
                        <p className="text-[11px] text-muted-foreground">{desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {memoError && (
                <div className="p-2 bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 text-xs rounded-lg">
                  {memoError}
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={memoLoading || !conversationId}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
              >
                {memoLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Generate {MEMO_TYPES.find(t => t.key === selectedType)?.label}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Rendered memo */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                  {MEMO_TYPES.find(t => t.key === memo.memo_type)?.label || 'Memo'}
                </span>
                <div className="flex items-center gap-1 ml-auto">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                  >
                    {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={handleExportPdf}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
                  >
                    <FileDown className="w-3 h-3" />
                    PDF
                  </button>
                </div>
              </div>
              <div ref={contentRef} className="prose-sm">
                <MarkdownMessage content={memo.memo} />
              </div>
              <button
                onClick={() => { clearMemo(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-border hover:bg-secondary/30 text-sm text-muted-foreground transition-colors"
              >
                Generate Another
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
